import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import type { ILoginFormInput, ILoginInput } from '@core-ports/inputs/login';
import { LoginFormFactory } from '@infra-adapters/forms/login';
import { AuthService } from '@infra-adapters/services/auth.service';
import { PermissionsService } from '@infra-adapters/services/permissions.service';
import { AuthBaseLayoutComponent } from '@infra-ui/components/auth/auth-base-layout/auth-base-layout.component';
import { COREUI_BUTTON_IMPORTS, COREUI_FORM_IMPORTS } from '@utils/coreui';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    AuthBaseLayoutComponent,
    ...COREUI_FORM_IMPORTS,
    ...COREUI_BUTTON_IMPORTS,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
})
export class LoginComponent {

  form!: FormGroup<ILoginFormInput>;
  isLoading = signal(false);

  constructor(
    private loginFormFactory: LoginFormFactory,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private router: Router,
  ) {
    this.form = this.loginFormFactory.generateForm();
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.isLoading.set(true);
      this.authService.login(this.form.value as ILoginInput).subscribe({
        next: (response) => {
          this.authService.setSession(response.data.token, response.data.refresh_token);
          this.permissionsService.clear();
          this.isLoading.set(false);
          const session = this.authService.UserSessionData();
          if (session?.must_change_password) {
            this.router.navigate(['/cambiar-contrasena-inicial']);
          } else {
            this.router.navigate(['/']);
          }
        },
        error: (error) => {
          console.error('Login error:', error);
          this.isLoading.set(false);
          this.loginFormFactory.applyBackendErrors(error, this.form);
        }
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  getErrorMessage(controlName: keyof ILoginFormInput): string | null {
    return this.loginFormFactory.getErrorMessage(controlName, this.form);
  }

  isValid(controlName: keyof ILoginFormInput): boolean {
    return this.loginFormFactory.isControlValid(controlName, this.form);
  }

  isInvalid(controlName: keyof ILoginFormInput): boolean {
    return this.loginFormFactory.isControlInvalid(controlName, this.form);
  }
  
}
